import { Box, Text, VStack } from '@enechain/ecloud-designsystem'

import { iconSize } from '~/constants/iconSize'

type Props = {
  /**
   * 状態を表す glyph。**色は付けずに渡す** (本 component が `Object.disabled` で包む)。
   * 意味は色ではなく glyph と文言が担う。
   */
  icon: React.ReactNode
  title: string
  /**
   * 説明。 改行 (`\n`) で複数行にできる (`pre-line`)。 無くてよい — 渡さない (空文字も同じ)
   * 時は説明の行ごと出さず、 タイトルとボタンの間に空の行の隙間を残さない
   * (`design/ui-feasibility/components/empty-panel.md`: 8080 は `__desc` の要素ごと置かない)。
   */
  description?: string
  /** 次の行動。無くてよい (`design/ui-feasibility/components/empty-panel.md`)。 */
  action?: React.ReactNode
}

/**
 * 面が丸ごと空の時に置く**空状態パネル**
 * (`design/ui-feasibility/components/empty-panel.md`、proto `.ecl-empty-panel`)。
 *
 * **理由と次の行動**を示すためのもの。値は DS `PageError` を正とする
 * (icon 80px / `Object.disabled`、title 20px/600、desc 14px/400・`pre-line`、
 * desc→ボタン 32px、ボタン = Tertiary)。DS に「空状態」部品は無く、`PageError`
 * 自身は `100vw` / `100vh` の器に `70vh` で中央寄せする作りなのでサイドメニューと
 * パンくずの内側に置けない。そのため見た目だけを写した独自部品になっている。
 *
 * ## 使いどころ — 表の空行とは**別物**
 *
 * | 状態 | 使うもの |
 * |---|---|
 * | 列が決まっていて**行だけ 0** | `MasterDataTable` の `emptyLabel` (= 1 行テキストの空行) |
 * | **面が丸ごと空** (表自体を出す意味が無い) | **本 component** |
 *
 * さらに面が空の中でも、**空の理由が 1 つで待てば解消する**なら DS `Alert` 1 本で
 * 足りる。本 component は**理由が複数あり、理由ごとに次の行動が変わる**時に使う。
 *
 * ## 色は状態で変えない
 *
 * アイコンは常に `Object.disabled` の 1 色 (DS `PageError` と同じ)。状態色 modifier
 * (`--info` / `--warning`) は 2026-07-29 にデザイナー指示で廃止済み。
 */
export const EmptyPanel = ({
  icon,
  title,
  description,
  action,
}: Props): React.ReactElement => (
  <VStack alignItems="center" gap="2" paddingX="4" paddingY="10">
    {/* 色は状態によらず Object.disabled 固定。状態は glyph と文言が担う。 */}
    <Box color="Object.disabled">{icon}</Box>
    <Text textStyle="emptyPanelTitle" color="Text.highEmphasis">
      {title}
    </Text>
    {/* desc は mid ではなく highEmphasis (DS PageError の指定)。
        複数行を改行で継ぐので pre-line。 */}
    {description !== undefined && description !== '' && (
      <Text
        textStyle="body"
        maxWidth="480px"
        color="Text.highEmphasis"
        lineHeight={1.5}
        textAlign="center"
        whiteSpace="pre-line"
      >
        {description}
      </Text>
    )}
    {/* desc から 32px (VStack の gap 8px + marginTop 24px)。size は既定のまま
        (空状態は「次に何をするか」を促す面なので sm に落とさない)。 */}
    {action !== undefined && <Box marginTop="6">{action}</Box>}
  </VStack>
)

/** 呼び出し側が glyph に当てる大きさ (DS `PageError` と同じ 80px)。 */
export const EMPTY_PANEL_ICON_SIZE = iconSize.emptyPanel
